/* BG: mobile header (unit 02). Menu drawer + search drawer on the 00-global shell (body[data-bg-drawer], .bg-overlay),
   optional sticky bar, cart count mirror. Uses Dawn global.js trapFocus/removeTrapFocus; predictive-search.js and
   search-form.js unchanged. The cart icon is bound by cart-drawer.js via [data-cart-sidebar] (not here). */

(() => {
  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DRAWER_MS = 500;
  const PANEL_MS = 350;

  const afterTransition = (el, ms, cb) => {
    if (REDUCED.matches) {
      cb();
      return;
    }
    let done = false;
    const finish = (event) => {
      if (event && event.target !== el) return;
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', finish);
      cb();
    };
    el.addEventListener('transitionend', finish);
    setTimeout(finish, ms + 50);
  };

  class BgDrawer extends HTMLElement {
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      // Dawn ModalDialog pattern: leave the section's stacking context (z-index 20) so the drawer (z 101) sits above
      // the global .bg-overlay (z 100) whatever the header section does (sticky, transforms).
      document.body.appendChild(this);

      this.onKeyUp = this.onKeyUp.bind(this);
      this.onOverlayClick = this.close.bind(this);
      this.querySelectorAll('[data-bg-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      document.querySelectorAll(this.dataset.opener).forEach((opener) =>
        opener.addEventListener('click', (event) => {
          event.preventDefault();
          this.isOpen ? this.close() : this.open(opener);
        })
      );
      DESKTOP.addEventListener('change', (event) => {
        if (event.matches && this.isOpen) this.close();
      });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open(opener) {
      if (this.isOpen || this.opening) return;
      this.opening = true;
      this.opener = opener;
      this.removeAttribute('hidden');
      // next frame, so the transform transition runs from the off-canvas position
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.opening = false;
          this.setAttribute('open', '');
          document.body.dataset.bgDrawer = this.dataset.kind;
          opener?.setAttribute('aria-expanded', 'true');
          document.addEventListener('keyup', this.onKeyUp);
          document.querySelector('[data-bg-overlay]')?.addEventListener('click', this.onOverlayClick);
          // focus once visible (visibility flips with the transform transition)
          afterTransition(this, DRAWER_MS, () => {
            if (this.isOpen) trapFocus(this, this.focusTarget());
          });
        });
      });
    }

    focusTarget() {
      return this;
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      if (document.body.dataset.bgDrawer === this.dataset.kind) delete document.body.dataset.bgDrawer;
      this.opener?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keyup', this.onKeyUp);
      document.querySelector('[data-bg-overlay]')?.removeEventListener('click', this.onOverlayClick);
      removeTrapFocus(this.opener && !DESKTOP.matches ? this.opener : null);
      afterTransition(this, DRAWER_MS, () => {
        if (this.isOpen || this.opening) return; // re-opened during the slide-out
        this.setAttribute('hidden', '');
        this.afterClose();
      });
    }

    afterClose() {}

    onKeyUp(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    }
  }

  class BgMenuDrawer extends BgDrawer {
    connectedCallback() {
      const first = !this.moved;
      super.connectedCallback();
      if (!first) return;
      this.querySelectorAll('.bg-menu-drawer__details > summary').forEach((summary) =>
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSubmenu(summary.parentElement);
        })
      );
      this.querySelectorAll('[data-bg-menu-back]').forEach((btn) =>
        btn.addEventListener('click', () => this.closeSubmenu(btn.closest('details')))
      );
    }

    openSubmenu(details) {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.bg-menu-drawer__panel');
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        details.classList.add('menu-opening');
        afterTransition(panel, PANEL_MS, () => {
          if (details.classList.contains('menu-opening')) trapFocus(panel, details.querySelector('[data-bg-menu-back]'));
        });
      });
    }

    closeSubmenu(details, instant = false) {
      if (!details || !details.open) return;
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.bg-menu-drawer__panel');
      details.classList.remove('menu-opening');
      summary.setAttribute('aria-expanded', 'false');
      const end = () => {
        if (details.classList.contains('menu-opening')) return;
        details.open = false;
      };
      if (instant) {
        end();
        return;
      }
      trapFocus(this, summary);
      afterTransition(panel, PANEL_MS, end);
    }

    openDetails() {
      return this.querySelector('.bg-menu-drawer__details.menu-opening');
    }

    onKeyUp(event) {
      if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
      const details = this.openDetails();
      if (details) this.closeSubmenu(details);
      else this.close();
    }

    afterClose() {
      this.querySelectorAll('.bg-menu-drawer__details[open]').forEach((details) => this.closeSubmenu(details, true));
    }
  }

  class BgSearchDrawer extends BgDrawer {
    focusTarget() {
      return this.querySelector('input[type="search"]') || this;
    }
  }

  /* Bar: optional sticky (setting "on_scroll", off by default = live) + cart count mirror. */
  class BgHeaderMobile extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (this.dataset.sticky === 'on_scroll' && this.section) this.initSticky();
      this.onCartUpdate = this.onCartUpdate.bind(this);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    initSticky() {
      this.section.classList.add('bg-hm-sticky');
      this.lastY = window.scrollY;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          const y = window.scrollY;
          const pastHeader = y > this.section.offsetTop + this.section.offsetHeight;
          this.section.classList.toggle('bg-hm-hidden', pastHeader && y > this.lastY);
          this.lastY = y;
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    onCartUpdate(event) {
      const count = event?.cartData?.item_count;
      if (typeof count === 'number') {
        this.setCount(count);
        return;
      }
      fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => this.setCount(cart.item_count))
        .catch(() => {});
    }

    setCount(count) {
      const text = this.querySelector('[data-bg-cart-count]');
      if (text) text.textContent = count < 100 ? count : '';
      const label = this.querySelector('[data-bg-cart-count-label]');
      if (label) label.textContent = label.textContent.replace(/\d+/, count);
    }
  }

  if (!customElements.get('bg-menu-drawer')) customElements.define('bg-menu-drawer', BgMenuDrawer);
  if (!customElements.get('bg-search-drawer')) customElements.define('bg-search-drawer', BgSearchDrawer);
  if (!customElements.get('bg-header-mobile')) customElements.define('bg-header-mobile', BgHeaderMobile);

  /* Theme editor: the drawers were moved to <body>, so remove the stale copies when the section re-renders/unloads. */
  document.addEventListener('shopify:section:unload', (event) => {
    document
      .querySelectorAll(`body > .bg-drawer[data-section="${event.detail.sectionId}"]`)
      .forEach((drawer) => {
        if (drawer.isOpen) drawer.close();
        drawer.remove();
      });
  });
})();
